import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { getUser } from '../auth'
import { api } from '../api'
import { useToast } from '../components/Toast'
import { useAsyncData } from '../hooks/useAsyncData'
import Spinner, { PageSpinner } from '../components/Spinner'
import ConfirmDialog from '../components/ConfirmDialog'
import {
  ACCOUNT_TYPE_COLORS, ACCOUNT_TYPE_LABELS, CATEGORY_COLORS, ENV_COLORS,
  locationLabel,
} from '../constants'
import Instant from '../components/Instant'

const STATUS_BADGE = {
  pending: 'badge-warning',
  associated: 'badge-success',
  disassociation_failed: 'badge-error',
}

function Field({ label, children }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide opacity-40">{label}</dt>
      <dd className="mt-0.5">{children ?? '—'}</dd>
    </div>
  )
}

// The password sits behind its own endpoint so reads stay deliberate — fetched on
// click, never as a side effect of opening the page.
function AccountCredentials({ accountId }) {
  const [password, setPassword] = useState(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const { show } = useToast()

  async function reveal() {
    setBusy(true)
    try {
      const creds = await api.getAccountCredentials(accountId)
      setPassword(creds.password ?? '')
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setBusy(false)
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(password)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      show('Could not copy to clipboard', 'error')
    }
  }

  if (password === null) {
    return (
      <button className="btn btn-xs btn-outline" onClick={reveal} disabled={busy} aria-busy={busy}>
        {busy ? <Spinner size="xs" /> : 'Show password'}
      </button>
    )
  }
  if (password === '') return <span className="text-xs opacity-40">no password set</span>
  return (
    <span className="flex items-center gap-2 flex-wrap">
      <code className="font-mono text-xs bg-base-300/60 rounded px-1.5 py-0.5 break-all">{password}</code>
      <button className="btn btn-xs btn-ghost" onClick={copy}>{copied ? 'copied' : 'copy'}</button>
    </span>
  )
}

export default function BundleDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [currentUserId] = useState(() => getUser()?.id ?? null)
  const [cancelling, setCancelling] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const { show } = useToast()

  const { data, loading, error, reload } = useAsyncData(async () => {
    // Devices are fetched separately and indexed by MDI: the bundle payload carries
    // only MDI strings, and widening it would change the API contract for every caller.
    const [bundle, devices] = await Promise.all([
      api.getBundle(id),
      api.getDevices(),
    ])
    return { bundle, byMdi: new Map(devices.map(d => [d.mdi, d])) }
  }, [id], { onError: err => show(err.message, 'error') })

  const bundle = data?.bundle
  const byMdi = data?.byMdi
  const isOwn = bundle && bundle.reserved_by === currentUserId

  async function cancelBundle() {
    setCancelling(true)
    try {
      const res = await api.cancelBundle(id)
      if (res?.disassociation_failed) {
        show(`Released, but these could not be disassociated: ${res.failed_mdis.join(', ')}`, 'error')
        reload()
        setConfirmCancel(false)
      } else {
        show('Bundle cancelled', 'success')
        navigate('/bundles')
      }
    } catch (err) {
      show(err.message, 'error')
      setConfirmCancel(false)
    } finally {
      setCancelling(false)
    }
  }

  if (loading) return <PageSpinner />

  if (error) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Link to="/bundles" className="link link-hover text-sm">← Bundles</Link>
        <div className="text-center py-20">
          <p className="opacity-60 mb-4">Failed to load bundle: {error.message}</p>
          <button className="btn btn-sm btn-primary" onClick={reload}>Retry</button>
        </div>
      </div>
    )
  }

  if (!bundle) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Link to="/bundles" className="link link-hover text-sm">← Bundles</Link>
        <div className="text-center py-20 opacity-40">Bundle not found</div>
      </div>
    )
  }

  const deviceCount = bundle.accounts.reduce((n, a) => n + a.device_mdis.length, 0)

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <Link to="/bundles" className="link link-hover text-sm opacity-60">← Bundles</Link>

      <div className="flex items-center gap-3 mt-2 mb-6 flex-wrap">
        <h1 className="text-2xl font-bold font-mono">{bundle.id.slice(0, 8)}…</h1>
        <span className={`badge ${STATUS_BADGE[bundle.association_status] ?? 'badge-neutral'}`}>
          {bundle.association_status}
        </span>
        <button
          className="btn btn-sm btn-ghost text-error ml-auto"
          disabled={cancelling || !isOwn}
          aria-busy={cancelling}
          title={isOwn ? undefined : "Only the tester who created a bundle can cancel it"}
          onClick={() => setConfirmCancel(true)}
        >
          {cancelling ? <Spinner size="xs" /> : 'Cancel bundle'}
        </button>
      </div>

      <ConfirmDialog
        open={confirmCancel}
        title="Cancel bundle"
        message={
          `Release ${bundle.accounts.length} account(s) and ${deviceCount} device(s), ` +
          'and disassociate them in Emporia Cloud?'
        }
        confirmLabel="Cancel bundle"
        busy={cancelling}
        onConfirm={cancelBundle}
        onClose={() => setConfirmCancel(false)}
      />

      <div className="rounded-box border border-base-300 p-5 mb-6">
        <h2 className="font-semibold mb-3">Bundle</h2>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <Field label="Status">{bundle.association_status}</Field>
          <Field label="Created"><Instant iso={bundle.created_at} /></Field>
          <Field label="Held by">
            {isOwn ? 'You' : (bundle.reserved_by_email ?? bundle.reserved_by)}
          </Field>
          <Field label="Accounts">{bundle.accounts.length}</Field>
          <Field label="Devices">{deviceCount}</Field>
          <Field label="Bundle ID"><span className="font-mono text-xs break-all">{bundle.id}</span></Field>
        </dl>
      </div>

      {bundle.association_status === 'disassociation_failed' && (
        <div className="rounded-box border border-error/40 bg-error/5 p-4 mb-6 text-sm">
          Reservations were released, but some devices could not be disassociated in
          Emporia Cloud. Those devices stay reserved so the pool cannot hand out a
          device an old account still has access to. Retry cancel, or clean up manually.
        </div>
      )}

      {bundle.accounts.map(acct => (
        <div
          key={acct.account_id ?? `${acct.account_email}-${acct.environment}`}
          className="rounded-box border border-base-300 p-5 mb-4"
        >
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {acct.account_id ? (
              <Link to={`/accounts/${acct.account_id}`} className="link link-hover font-semibold break-all">
                {acct.account_email}
              </Link>
            ) : (
              <span className="font-semibold break-all">{acct.account_email}</span>
            )}
            {!acct.account_id && (
              <span className="badge badge-sm badge-warning" title="Not a QA inventory account">
                external
              </span>
            )}
            <span className={`badge badge-sm ${ACCOUNT_TYPE_COLORS[acct.account_type] ?? 'badge-ghost'}`}>
              {ACCOUNT_TYPE_LABELS[acct.account_type] ?? acct.account_type}
            </span>
            <span className={`badge badge-sm ${ENV_COLORS[acct.environment] ?? 'badge-neutral'}`}>
              {acct.environment}
            </span>
            {acct.internal_ring && (
              <span className="badge badge-sm badge-accent" title="In the PostHog QA internal ring for this bundle">
                internal ring
              </span>
            )}
            <span className="ml-auto">
              {acct.account_id
                ? <AccountCredentials accountId={acct.account_id} />
                : <span className="text-xs opacity-40">credentials not held here</span>}
            </span>
          </div>

          {acct.device_mdis.length === 0 ? (
            <p className="text-sm opacity-40">No devices on this account.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>MDI</th><th>Category</th><th>Model</th><th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {acct.device_mdis.map(mdi => {
                    const d = byMdi?.get(mdi)
                    return (
                      <tr key={mdi}>
                        <td className="font-mono text-xs">
                          {d ? (
                            <Link to={`/devices/${d.id}`} className="link link-hover">{mdi}</Link>
                          ) : mdi}
                        </td>
                        <td>
                          {d ? (
                            <span className={`badge badge-sm ${CATEGORY_COLORS[d.category] ?? 'badge-neutral'}`}>
                              {d.category}
                            </span>
                          ) : <span className="opacity-30">—</span>}
                        </td>
                        <td>{d?.model ?? <span className="opacity-30">—</span>}</td>
                        <td className="text-xs opacity-70">
                          {d?.location ? locationLabel(d.location) : <span className="opacity-30">—</span>}
                          {d?.is_virtual && <span className="badge badge-xs badge-ghost ml-1">virtual</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
